import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ShieldAlert, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Global banner shown when the current session was minted by an admin
 * impersonating another user. Renders nothing when not impersonating.
 * Sticks to the top of every route so the admin never forgets they're not on
 * their own account.
 */
export default function ImpersonationBanner() {
  const { user, checkAuth } = useAuth();
  const [busy, setBusy] = useState(false);

  const imp = user?.impersonation;
  if (!imp?.active) return null;

  const stop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/admin/stop-impersonate");
      await checkAuth();
      toast.success("Returned to admin account");
      // Full reload to purge any per-user cached state (feed, wallet, etc).
      setTimeout(() => {
        window.location.href = "/";
      }, 150);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not return to admin account"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="sticky top-0 z-[70] w-full bg-[#FF007F] text-black border-b border-black/20"
    >
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2 md:pl-64">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        <p className="font-mono text-[11px] uppercase tracking-widest flex-1 truncate">
          Admin impersonation active — you are signed in as{" "}
          <span className="font-bold">@{user?.username}</span>
        </p>
        <button
          onClick={stop}
          disabled={busy}
          data-testid="impersonation-stop-btn"
          className="shrink-0 flex items-center gap-1.5 bg-black text-[#FF007F] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-black/80 disabled:opacity-50 transition-all"
        >
          <LogOut className="h-3 w-3" />
          {busy ? "Returning…" : "Return to admin"}
        </button>
      </div>
    </div>
  );
}
